import axios from 'axios';
import i18n from '../i18n';
import url from '../url.js';

/**
 * 獲取所有模板列表
 * @param {string} type - 可選的類型過濾 ('all', 'guided-flow', 'activity-template')
 * @param {string} [lang] - 語言代碼（如 'en', 'zh-TW'）；省略時由後端使用預設值
 * @returns {Promise<Array>} 模板列表
 */
export const getTemplates = async (type = 'all', lang) => {
  try {
    const params = {};
    if (type !== 'all') params.type = type;
    if (lang) params.lang = lang;
    const response = await axios.get(
      `${url.backendHost}api/templates`,
      { 
        params,
        withCredentials: true 
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching templates:', error);
    throw new Error(error.response?.data?.message || '獲取模板列表失敗');
  }
};

/**
 * 根據 ID 獲取模板詳情
 * @param {string} templateId - 模板 ID
 * @param {string} [lang] - 語言代碼（如 'en', 'zh-TW'）；省略時由後端使用預設值
 * @returns {Promise<Object>} 模板詳情
 */
export const getTemplateById = async (templateId, lang) => {
  try {
    const params = lang ? { lang } : {};
    const response = await axios.get(
      `${url.backendHost}api/templates/${templateId}`,
      { params, withCredentials: true }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching template:', error);
    throw new Error(error.response?.data?.message || '獲取模板詳情失敗');
  }
};

/**
 * 基於模板創建活動和夥伴
 * @param {string} templateId - 模板 ID
 * @param {string} userId - 用戶 ID
 * @returns {Promise<Object>} 創建結果
 */
export const createFromTemplate = async (templateId, userId, lang = i18n.language) => {
  try {
    const jwtToken = localStorage.getItem('jwtToken');
    const headers = { 
      Authorization: `Bearer ${jwtToken}`,
      'Content-Type': 'application/json'
    };

    const response = await axios.post(
      `${url.backendHost}api/activities/createFromTemplate`,
      { templateId, userId, lang },
      { headers, withCredentials: true }
    );

    // 更新 JWT token（如果返回了新 token）
    if (response.data.jwtToken) {
      localStorage.setItem('jwtToken', response.data.jwtToken);
    } else {
      // 如果沒有返回 token，調用 renew API 更新
      try {
        const renewResponse = await axios.get(
          `${url.backendHost}api/users/renew`,
          { headers, withCredentials: true }
        );
        if (renewResponse.data.jwtToken) {
          localStorage.setItem('jwtToken', renewResponse.data.jwtToken);
        }
      } catch (renewError) {
        console.warn('Failed to renew token:', renewError);
      }
    }

    return response.data;
  } catch (error) {
    console.error('Error creating from template:', error);
    throw new Error(error.response?.data?.message || '創建活動失敗');
  }
};

/**
 * 通過引導流程創建活動
 * @param {Object} activityData - 活動數據
 * @param {string} activityData.userId - 用戶 ID
 * @param {Object} activityData.activity - 活動基本資訊
 * @param {Array} activityData.initialNodes - 引導單元列表（簡單格式）
 * @param {Array} activityData.nodeGroups - 分組節點列表（複雜格式）
 * @param {Array} activityData.selectedPartners - 選擇的 AI 夥伴名稱列表
 * @returns {Promise<Object>} 創建結果
 */
export const createGuidedActivity = async (activityData) => {
  try {
    const jwtToken = localStorage.getItem('jwtToken');
    const headers = { 
      Authorization: `Bearer ${jwtToken}`,
      'Content-Type': 'application/json'
    };

    const response = await axios.post(
      `${url.backendHost}api/activities/createGuided`,
      {
        ...activityData,
        lang: activityData?.lang || i18n.language
      },
      { headers, withCredentials: true }
    );

    // 更新 JWT token（如果返回了新 token）
    if (response.data.jwtToken) {
      localStorage.setItem('jwtToken', response.data.jwtToken);
    } else {
      // 如果沒有返回 token，調用 renew API 更新
      try {
        const renewResponse = await axios.get(
          `${url.backendHost}api/users/renew`,
          { headers, withCredentials: true }
        );
        if (renewResponse.data.jwtToken) {
          localStorage.setItem('jwtToken', renewResponse.data.jwtToken);
        }
      } catch (renewError) {
        console.warn('Failed to renew token:', renewError);
      }
    }

    return response.data;
  } catch (error) {
    console.error('Error creating guided activity:', error);
    throw new Error(error.response?.data?.message || '創建活動失敗');
  }
};
